import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from "rxjs";
import { UntypedFormGroup } from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { VolumeCalculatorComponent } from "../../form-parts/volume-calculator/volume-calculator.component";
import { FormService } from "../../services/form.service";
import { ApiService } from "../../services/api.service";

@Component({
    selector: 'app-moving-content',
    templateUrl: './moving-content.component.html',
    styleUrls: ['./moving-content.component.scss']
})
export class MovingContentComponent implements OnInit, OnDestroy {

    movingContent !: UntypedFormGroup
	formGroupName : string = 'movingContent'
	vcPreCategories: any | undefined;
	private vcItemsRequested : boolean = false;

	// This component sits behind an *ngIf ( app.component.html: movingSize !== 4 ), so it
	// is destroyed and rebuilt whenever the visitor changes their moving size across that
	// boundary. Without this the stepChange$ subscription of every dead instance would
	// stay registered on the Subject and fire again alongside the live one.
	private destroy$ = new Subject<void>();

    constructor( private dialog: MatDialog,
                 public formService: FormService,
                 private apiService: ApiService ) { }

    public ngOnInit(): void {

        this.movingContent = this.formService.mainForm.get( this.formGroupName ) as UntypedFormGroup;

		// This component is constructed on page load but stays hidden behind [style.display]
		// until the user reaches this step, so anything fetched here would be fetched for
		// every visitor on step one — including the crawlers that make up most of the traffic
		// and then refuse the request themselves. Wait for a step change instead.
		//
		// Fetch on the PREVIOUS step, not on this one: the volume-calculator link is
		// rendered as soon as this step is shown, and the dialog receives a snapshot of
		// vcCategories, so a click during the round trip would open a calculator that never
		// fills. Starting one step earlier closes that window. Same pattern, and the same
		// reasoning, as name-email-phone.component.ts. Crawlers do not press Continue, so
		// they never reach movingSize either.
		this.formService.stepChange$
			.pipe( takeUntil( this.destroy$ ) )
			.subscribe( currentFormGroupName => {

				if( currentFormGroupName === 'movingSize' || currentFormGroupName === this.formGroupName ) {

					this.getVcItems();
				}
			} );

		// The pre-filled items are a special case and stay on init: they also pre-set
		// movingSize, which is an earlier step, so fetching them later would arrive after
		// the user has already answered it. They need a _vcToken from an emailed link,
		// which crawlers do not have, so they are not part of the bot traffic.
		this.getVcPreItems();
    }

	public ngOnDestroy(): void {

		this.destroy$.next();
		this.destroy$.complete();
	}

    ngAfterViewInit() {

        // After view has loaded
    }

    public onSubmit() {

        this.formService.submitFormGroup( (<UntypedFormGroup>this.movingContent) );
    }

    public openVolumeCalculator() {

        const dialogConfig = new MatDialogConfig();
		dialogConfig.height = 'calc( 100vh - 55px )';
		dialogConfig.width = '920px';
		dialogConfig.backdropClass = 'vc-blurred-bg';
		dialogConfig.panelClass = 'vc-dialog';

		// Pass data
		dialogConfig.data = { vcCategories: this.formService.vcCategories, vcPreCategories: this.vcPreCategories };

	    this.dialog.open(VolumeCalculatorComponent, dialogConfig );
    }

	public hasVolume() {

		return this.movingContent.controls['volumeM3'].value > 0;
	}

	public getMovingSize() {

		return Number( (<UntypedFormGroup>this.formService.mainForm.get( 'movingSize' ) ).controls['movingSize'].value );
	}

	public getVolumeText() : string {

		var output = '';

		for( var item of this.formService.getVolumeOrder() ) {

			if( item === 'm3' ) {

				output += this.movingContent.controls['volumeM3'].value + 'm³';
			}

			if( item === 'divider' ) {

				output += ' / ';
			}

			if( item === 'ft3' ) {

				output += this.movingContent.controls['volumeFt3'].value + 'ft³';
			}
		}

		return output;
	}

	public getVcItems() {

		// stepChange$ fires again every time the user walks back and forward through the
		// form, and the item list never changes within a session.
		if( this.vcItemsRequested ) {

			return;
		}

		// Set before the request, not after it: stepChange$ can emit again while this one is
		// still in flight, and waiting for the response would let a second request out.
		this.vcItemsRequested = true;

		// No error branch needed: ApiService.getVcItems() reports what is worth reporting
		// and then completes quietly, so the form carries on without the calculator. That
		// also means a failure arrives as completion without a value, which is what the
		// stored flag below detects — clear the guard so re-entering the step tries again
		// rather than leaving the calculator gone for the rest of the session.
		var stored = false;

		this.apiService.getVcItems().subscribe( {

			next: response => {

				this.formService.vcCategories = response;
				this.formService.setupVolumeCalculatorForm( response );
				stored = true;
			},
			complete: () => {

				if( ! stored ) {

					this.vcItemsRequested = false;
				}
			},
		} );
	}

	public getVcPreItems() {

		// If there's a vc token
		var token = (<UntypedFormGroup>this.formService.mainForm).controls['_vcToken'].value;

		if( token.length > 0 ) {

			this.apiService.getVcPreItems( token ).subscribe( (response: any) => {

					if( response ) {

						// Prepare for vc
						this.vcPreCategories = response.vc;

						// Pre-set movingSize
						var movingSizeControl = (<UntypedFormGroup>this.formService.mainForm.controls['movingSize']).controls['movingSize'];

						// If the pre-set volume is more than 0
						if( response.m3 > 0 ) {

							if( response.m3 <= 4 ) {

								movingSizeControl.setValue( 3 );
							}
							else if( response.m3 <= 12 ) {

								movingSizeControl.setValue( 2 );
							}
							else {

								movingSizeControl.setValue( 1 );
							}

							// Set ft3 and m3 from email
							this.movingContent.controls['volumeM3'].setValue( response.m3 );
							this.movingContent.controls['volumeFt3'].setValue( response.ft3 );
						}
					}
			} );
		}
	}
}